/**
 * useEmailBehavioralActionsData
 *
 * P1 SLICE 3: data hook for the "Actions" view, backed by the real,
 * already-merged `GET /email-delivery/dashboard/behavioral-actions`
 * contract (P1 IMPLEMENTATION SLICE 2, PR #358).
 *
 * Unlike useEmailDashboardData.js/useEmailPerformanceData.js, this hook
 * only ever makes ONE real request (a single backend domain, not an
 * EDT+LPE merge) — there is no per-stage defensive null-on-failure
 * pattern here; a failed request surfaces as this hook's own `error`,
 * exactly like any other single-domain fetch elsewhere in this codebase.
 * A successful response with every metric's `value` at 0 is valid data,
 * never treated as an error or an empty state.
 *
 * Accepts the SAME seven governed filters Overview/Performance already
 * use, with the SAME per-domain Environment mapping
 * useEmailPerformanceData.js's own header comment documents: this
 * endpoint is LPE-side (same governed Broadcast environment
 * `/lpe-funnel` already uses), so `environment` maps to `lpeEnvironment`
 * only — this hook never sends an EDT-side `observedGhlEnvironment`
 * parameter of any kind, because it never calls the EDT domain at all.
 *
 * Returns the eight frozen Behavioral LPE action metrics verbatim
 * (`metrics.<metric_id>.{value, grain, attempts_with_action,
 * journeys_with_action}`) — no reshaping, no derived rollups, no
 * fabricated fallback metric.
 */
import { useState, useEffect, useCallback } from "react";
import { getEmailDeliveryBehavioralActionsSummary } from "../lib/emailDeliveryBehavioralActionsApi";

export function useEmailBehavioralActionsData(options = {}) {
  const {
    period = "mtd",
    customRange = null,
    environment = null,
    governedBroadcastId = null,
    governedAudienceId = null,
    governedProgramId = null,
    governedCampaignId = null,
    governedState = null,
    enabled = true,
  } = options;

  // Initialize as loading (when enabled) so the view renders its loading
  // skeleton on first paint instead of briefly flashing the empty state —
  // same reasoning as useEmailDashboardData/useEmailPerformanceData.
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);
  const [metrics, setMetrics] = useState(null);

  const lpeEnvironment = environment && environment !== "all" ? environment : null;

  const fetchData = useCallback(async () => {
    if (!enabled) return;

    setLoading(true);
    setError(null);

    try {
      const response = await getEmailDeliveryBehavioralActionsSummary({
        period,
        startDate: period === "custom" ? customRange?.startDate : undefined,
        endDate: period === "custom" ? customRange?.endDate : undefined,
        broadcastId: governedBroadcastId || undefined,
        audienceId: governedAudienceId || undefined,
        programId: governedProgramId || undefined,
        campaignId: governedCampaignId || undefined,
        state: governedState || undefined,
        environment: lpeEnvironment || undefined,
      });
      setMetrics(response);
    } catch (err) {
      setError(err?.message || "Failed to load Email Behavioral Actions data");
    } finally {
      setLoading(false);
    }
  }, [
    period,
    customRange,
    governedBroadcastId,
    governedAudienceId,
    governedProgramId,
    governedCampaignId,
    governedState,
    lpeEnvironment,
    enabled,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    loading,
    error,
    metrics,
    refresh: fetchData,
  };
}

export default useEmailBehavioralActionsData;
